import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { currentSession } from "../services/authService.js";
import type { SessionView } from "../services/http.js";

/**
 * Who is signed in, if anybody.
 *
 * # Three states, not two
 *
 * `undefined` while the answer is still in flight, `null` for **nobody**, and a
 * session otherwise. Collapsing the first two is what makes a front door flash
 * "Sign in to start a party" at somebody who is already signed in — and on a
 * page whose whole job is to tell you what you can do, a wrong first frame is
 * worse than a slower one.
 *
 * # Not signed in is the normal case
 *
 * Most people who touch sonolume never make an account (ADR 0007). `null` is
 * therefore an answer rather than an error, and only a genuinely broken request
 * sets `error`.
 */
export interface Session {
  /** `undefined` while loading, `null` when nobody is signed in. */
  account: SessionView | null | undefined;
  /** Something went wrong asking — not the same as "nobody is signed in". */
  error: string | null;
  /** Ask again — after signing in or out. */
  refresh: () => void;
}

export function useSession(): Session {
  const { t } = useTranslation();
  const [account, setAccount] = useState<SessionView | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const refresh = useCallback(() => {
    setAccount(undefined);
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const session = await currentSession();
        if (!cancelled) {
          setAccount(session);
          setError(null);
        }
      } catch (cause) {
        if (!cancelled) {
          // A failure here must not lock somebody out of the parts of this app
          // that need no account at all, so the page still renders and this is
          // only reported.
          setAccount(null);
          setError(cause instanceof Error ? cause.message : t("session.unreachable"));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // `t` is a dependency because the effect words its own failure; asking the
    // server who is signed in again after a language switch costs one request.
  }, [attempt, t]);

  return { account, error, refresh };
}
